/** Bulk Asset Upload dialog — Download Template + Upload Excel + Preview +
 *  Inline Correction + Commit.
 *
 *  The preview table now lets operators fix each row's Status, Serial,
 *  Tag, Assignee and Location right inside the dialog — no export, no
 *  re-upload.  Every save re-validates on the server and refreshes the
 *  summary counts so the "Ready" number moves in real time.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, Upload, Loader2, AlertCircle, CheckCircle2, X, Pencil, Save, Wand2,
} from "lucide-react";
import AssigneeTypeahead from "@/components/app/AssigneeTypeahead";

const ALLOWED_STATUS = ["Active", "Inactive", "In Use", "In Transit",
                          "Under Repair", "Retired", "In Storage"];

// Dry-run plan colour tones so operators can see at a glance what the
// commit will actually do — CREATE (new record), UPDATE (serial
// already exists → would update), or SKIP (row won't be written).
const PLAN_TONE = {
  CREATE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  UPDATE: "bg-sky-100    text-sky-800    border-sky-200",
  SKIP:   "bg-slate-100  text-slate-700  border-slate-200",
};

// Fields we let the operator fix inline (canonical header names).
const EDITABLE_FIELDS = [
  { key: "Customer",     label: "Customer",     type: "text",
    hint: "Master customer name or code" },
  { key: "ABM ID",       label: "ABM ID",       type: "text" },
  { key: "Asset Tag",    label: "Tag",          type: "text" },
  { key: "Category",     label: "Category",     type: "text" },
  { key: "Product",      label: "Product",      type: "text" },
  { key: "Serial",       label: "Serial",       type: "text" },
  { key: "State",        label: "State",        type: "select",
    options: ALLOWED_STATUS },
  { key: "Warranty End", label: "Warranty End", type: "text",
    hint: "YYYY-MM-DD" },
  { key: "Country",      label: "Country",      type: "text" },
];

export default function BulkAssetUploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [session, setSession] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [editingRow, setEditingRow] = useState(null); // excel_row of row in edit mode
  const [draft, setDraft] = useState({});
  const [savingRow, setSavingRow] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkFixOpen, setBulkFixOpen] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [masters, setMasters] = useState([]);
  const [subs, setSubs] = useState([]);
  const [masterId, setMasterId] = useState("");
  const [companyId, setCompanyId] = useState("");
  // Tracks the (master, company) that were in effect when the most-recent
  // parse succeeded, so we can trigger exactly one re-parse when the user
  // switches to a different tenant — never on mount, never in a loop.
  const lastAppliedRef = useRef({ masterId: null, companyId: null });

  const reset = () => {
    setFile(null); setSession(null); setFilter("ALL");
    setEditingRow(null); setDraft({});
    setSelectedRows(new Set()); setBulkFixOpen(false); setBulkAssignee("");
    setMasterId(""); setCompanyId(""); setSubs([]);
    lastAppliedRef.current = { masterId: null, companyId: null };
  };

  // Load master companies once the dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/master-companies");
        setMasters(data.items || []);
      } catch { /* silent — picker is optional */ }
    })();
  }, [open]);

  // Cascade subsidiaries whenever the master changes
  useEffect(() => {
    if (!masterId) { setSubs([]); setCompanyId(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/companies",
          { params: { master_company_id: masterId } });
        setSubs(data.items || []);
        setCompanyId("");
      } catch { setSubs([]); }
    })();
  }, [masterId]);

  const toggleRow = (excelRow) => setSelectedRows((s) => {
    const next = new Set(s);
    if (next.has(excelRow)) next.delete(excelRow); else next.add(excelRow);
    return next;
  });

  const runBulkFix = async () => {
    if (!session || !bulkAssignee.trim() || !selectedRows.size) return;
    setBulkBusy(true);
    const ids = Array.from(selectedRows);
    let ok = 0;
    try {
      for (const excelRow of ids) {
        try {
          const { data } = await api.patch(
            `/assets/bulk/session/${session.session_id}/row/${excelRow}`,
            { values: { "Assigned To": bulkAssignee.trim() } });
          setSession((s) => ({
            ...s, counts: data.counts,
            rows: (s.rows || []).map((r) =>
              r.excel_row === excelRow ? data.row : r),
          }));
          ok += 1;
        } catch { /* keep going */ }
      }
      toast.success(`Bulk fix applied to ${ok}/${ids.length} row(s)`);
      setSelectedRows(new Set()); setBulkFixOpen(false); setBulkAssignee("");
    } finally { setBulkBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/assets/bulk/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "SUNTRONIX_ONE_Device_Bulk_Upload_Template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const parse = useCallback(async (f) => {
    if (!f) return;
    setFile(f); setSession(null); setParsing(true);
    const fd = new FormData();
    fd.append("file", f);
    if (masterId) fd.append("master_company_id", masterId);
    if (companyId) fd.append("company_id", companyId);
    try {
      const { data } = await api.post("/assets/bulk/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" }});
      setSession(data);
      // Remember which tenant selection produced this session so the
      // re-parse effect only fires on a genuine tenant change.
      lastAppliedRef.current = { masterId, companyId };
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "string" && detail.length
          ? detail
          : "Could not parse file — please download the latest template and try again.",
        { duration: 12_000 });
    } finally { setParsing(false); }
  }, [masterId, companyId]);

  // Re-parse when the operator switches Master or Subsidiary AFTER the
  // first successful parse.  Uses a ref (not `session`/`file` deps) so
  // we never enter the setSession → effect → parse → setSession loop
  // that used to leave the dialog stuck on the spinner.
  useEffect(() => {
    const last = lastAppliedRef.current;
    if (last.masterId === null && last.companyId === null) return; // no parse yet
    if (last.masterId === masterId && last.companyId === companyId) return; // no change
    if (file) parse(file);
  }, [masterId, companyId, file, parse]);

  const commit = async () => {
    if (!session) return;
    setCommitting(true);
    try {
      const body = { session_id: session.session_id };
      if (masterId) body.master_company_id = masterId;
      if (companyId) body.company_id = companyId;
      const { data } = await api.post("/assets/bulk/commit", body);
      toast.success(`${data.inserted} devices added, ${data.skipped} skipped`);
      onDone?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setCommitting(false); }
  };

  const startEdit = (row) => {
    setEditingRow(row.excel_row);
    // Seed the draft from either the original spreadsheet values or the
    // normalised values (so a good row can still be tweaked).
    const original = row.original || {};
    const n = row.normalised || {};
    setDraft({
      "Asset ID / Tag": original["Asset ID / Tag"] ?? n.asset_tag ?? "",
      "Device Name":    original["Device Name"] ?? n.product ?? "",
      "Serial Number":  original["Serial Number"] ?? n.serial ?? "",
      "Status":         original["Status"] ?? n.status ?? "",
      "Grade":          original["Grade"] ?? n.condition_grade ?? "",
      "Assigned To":    original["Assigned To"] ?? n.assignee_name ?? "",
    });
  };

  const cancelEdit = () => { setEditingRow(null); setDraft({}); };

  // Inline promotion: when a row's customer isn't found in the tenant
  // master, the operator can create it on the fly and re-validate.
  const createMasterFromRow = async (row) => {
    const proposed = (row.original?.["Customer"]
                        || row.original?.["Client / Subsidiary"]
                        || "").trim();
    if (!proposed) {
      toast.error("This row has no customer name — please fix it inline first.");
      return;
    }
    try {
      const { data: created } = await api.post("/master-companies", {
        name: proposed,
        legal_name: proposed,
      });
      toast.success(`Created "${proposed}" as a new master — refreshing rows…`);
      // Refresh master picker, apply the new master, and re-parse.
      try {
        const { data } = await api.get("/master-companies");
        setMasters(data.items || []);
      } catch { /* ignore */ }
      setMasterId(created.id || created.master_company_id || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create master");
    }
  };

  const saveEdit = async (excelRow) => {
    if (!session) return;
    setSavingRow(true);
    try {
      const { data } = await api.patch(
        `/assets/bulk/session/${session.session_id}/row/${excelRow}`,
        { values: draft });
      // Merge the fresh row + new counts into the local session
      setSession((s) => ({
        ...s,
        counts: data.counts,
        rows: (s.rows || []).map((r) =>
          r.excel_row === excelRow ? data.row : r),
      }));
      toast.success(data.row.result === "READY"
        ? `Row ${excelRow} · fixed`
        : `Row ${excelRow} updated — still ${data.row.result.toLowerCase()}`);
      setEditingRow(null); setDraft({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save row");
    } finally { setSavingRow(false); }
  };

  const rows = useMemo(() => session?.rows || [], [session?.rows]);
  const filtered = filter === "ALL"
    ? rows
    : filter.startsWith("PLAN_")
      ? rows.filter((r) => (r.planned_action || "SKIP") === filter.slice(5))
      : rows.filter((r) => r.result === filter);
  const unresolvedCount = useMemo(() =>
    rows.filter((r) => {
      const n = r.normalised || {};
      return n.assignee_hint && !n.current_employee_id && !n.assignee_kind;
    }).length, [rows]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle>Bulk upload devices</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2 border-b pb-3">
          <Button variant="outline" size="sm" onClick={downloadTemplate}
            data-testid="bulk-download-template">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download template
          </Button>
          <label className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 hover:bg-slate-50 px-3 py-1.5 text-[12px] font-medium cursor-pointer"
            data-testid="bulk-upload-picker">
            <Upload className="h-3.5 w-3.5" strokeWidth={1.8} />
            {file ? file.name : "Select .xlsx / .csv"}
            <input type="file" className="sr-only"
              accept=".xlsx,.csv"
              onChange={(e) => parse(e.target.files?.[0])} />
          </label>

          {/* Cascading Master → Subsidiary picker.  Session-level default
              applied to every row that doesn't resolve a Client / Subsidiary
              cell on its own. */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">
              Master Customer
            </label>
            <select
              className="border rounded px-2 py-1 text-[12px] min-w-[180px]"
              value={masterId}
              onChange={(e) => setMasterId(e.target.value)}
              data-testid="bulk-master-picker">
              <option value="">(none — resolve per row)</option>
              {masters.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">
              Client (Subsidiary)
            </label>
            <select
              className="border rounded px-2 py-1 text-[12px] min-w-[180px]"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              disabled={!masterId}
              data-testid="bulk-subsidiary-picker">
              <option value="">
                {masterId ? "(all rows use this)" : "(pick Master first)"}
              </option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {parsing && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          {session && (
            <div className="ml-auto text-[11px] text-slate-500">
              Tip: click <Pencil className="inline h-3 w-3 mx-0.5" /> to fix a
              row inline — no re-upload needed.
            </div>
          )}
        </div>

        {session && (
          <div className="flex flex-col overflow-hidden flex-1 gap-3">
            <div className="grid grid-cols-5 gap-2 text-[12px]">
              {[
                ["Total", session.counts.total, "ALL"],
                ["Ready", session.counts.ready, "READY"],
                ["Errors", session.counts.errors, "ERROR"],
                ["Duplicates", session.counts.duplicates, "DUPLICATE"],
                ["Warnings", session.counts.warnings, "WARN"],
              ].map(([label, n, key]) => (
                <button key={label}
                  onClick={() => setFilter(key === "WARN" ? "ALL" : key)}
                  className={"st-panel p-2 text-left border " +
                    (filter === key ? "border-slate-900" : "border-slate-200")}
                  data-testid={`bulk-summary-${label.toLowerCase()}`}>
                  <div className="text-[10px] uppercase text-slate-500">{label}</div>
                  <div className="text-lg font-heading font-semibold">{n}</div>
                </button>
              ))}
            </div>

            {/* Dry-run plan strip — shows exactly what a Commit would do */}
            {session.counts?.plan && (
              <div className="rounded-md border border-slate-200 bg-white p-2.5"
                   data-testid="bulk-dry-run-plan">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                    Dry-run — if you Commit now
                  </div>
                  <div className="text-[10px] text-slate-400">
                    Click a tile to filter rows
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "PLAN_CREATE", label: "Create",       value: session.counts.plan.create, tone: "emerald", desc: "brand-new devices" },
                    { key: "PLAN_UPDATE", label: "Would update", value: session.counts.plan.update, tone: "sky",     desc: "serial already in registry" },
                    { key: "PLAN_SKIP",   label: "Skip",         value: session.counts.plan.skip,   tone: "slate",   desc: "blocking errors" },
                  ].map((t) => (
                    <button key={t.key}
                      onClick={() => setFilter((f) => f === t.key ? "ALL" : t.key)}
                      data-testid={`bulk-plan-${t.label.toLowerCase().replace(" ","-")}`}
                      className={"text-left px-2.5 py-1.5 rounded border transition " +
                        (filter === t.key ? "border-slate-900 ring-1 ring-slate-900 " : "border-slate-200 hover:border-slate-400 ") +
                        (t.tone === "emerald" ? "bg-emerald-50" :
                         t.tone === "sky"     ? "bg-sky-50"     : "bg-slate-50")}>
                      <div className={"text-[10px] uppercase tracking-[0.14em] " +
                        (t.tone === "emerald" ? "text-emerald-700" :
                         t.tone === "sky"     ? "text-sky-700"     : "text-slate-600")}>
                        {t.label}
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <div className={"text-lg font-heading font-semibold " +
                          (t.tone === "emerald" ? "text-emerald-800" :
                           t.tone === "sky"     ? "text-sky-800"     : "text-slate-800")}>
                          {t.value}
                        </div>
                        <div className="text-[10px] text-slate-500">{t.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="overflow-y-auto flex-1 border rounded-md">
              {(unresolvedCount > 0 || selectedRows.size > 0) && (
                <div className="sticky top-0 z-10 bg-amber-50 border-b border-amber-200 px-3 py-2 flex items-center gap-2 text-[12px]"
                      data-testid="bulk-multi-fix-bar">
                  <Wand2 className="h-3.5 w-3.5 text-amber-700" />
                  <span className="text-amber-900">
                    {selectedRows.size > 0
                      ? `${selectedRows.size} row(s) selected`
                      : `${unresolvedCount} row(s) have unresolved assignees`}
                  </span>
                  {selectedRows.size > 0 && (
                    <Button size="sm" onClick={() => setBulkFixOpen(true)}
                      data-testid="bulk-multi-fix-open"
                      className="ml-auto bg-amber-700 hover:bg-amber-800 text-white">
                      Fix {selectedRows.size} assignee(s)…
                    </Button>
                  )}
                  {selectedRows.size === 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedRows(new Set(
                        rows.filter((r) => {
                          const n = r.normalised || {};
                          return n.assignee_hint && !n.current_employee_id && !n.assignee_kind;
                        }).map((r) => r.excel_row)))}
                      data-testid="bulk-multi-fix-select-all"
                      className="ml-auto text-[11px] underline underline-offset-2 text-amber-800">
                      Select all unresolved
                    </button>
                  )}
                </div>
              )}
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left w-6"></th>
                    <th className="px-2 py-1.5 text-left w-10">Row</th>
                    {EDITABLE_FIELDS.map((f) => (
                      <th key={f.key} className="px-2 py-1.5 text-left">
                        {f.label}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 text-left">Result</th>
                    <th className="px-2 py-1.5 text-left">Issues</th>
                    <th className="px-2 py-1.5 text-right w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const isEditing = editingRow === r.excel_row;
                    const n = r.normalised || {};
                    const o = r.original || {};
                    return (
                      <tr key={r.excel_row} className={"border-t " +
                        (isEditing ? "bg-amber-50/50" : r.edited ? "bg-sky-50/40" : "")}
                        data-testid={`bulk-row-${r.excel_row}`}>
                        <td className="px-2 py-1.5 align-top">
                          <input type="checkbox"
                                     checked={selectedRows.has(r.excel_row)}
                                     onChange={() => toggleRow(r.excel_row)}
                                     data-testid={`bulk-row-select-${r.excel_row}`} />
                        </td>
                        <td className="px-2 py-1.5 font-mono align-top">{r.excel_row}</td>

                        {/* Editable / read-only cells */}
                        {EDITABLE_FIELDS.map((f) => (
                          <td key={f.key} className="px-2 py-1.5 align-top">
                            {isEditing ? (
                              f.type === "select" ? (
                                <select
                                  className="w-full border rounded px-1 py-0.5 text-[12px]"
                                  value={draft[f.key] || ""}
                                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                                  data-testid={`bulk-edit-${r.excel_row}-${f.label.toLowerCase()}`}>
                                  <option value="">(default)</option>
                                  {f.options.map((op) => (
                                    <option key={op} value={op}>{op}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  className="w-full border rounded px-1 py-0.5 text-[12px]"
                                  value={draft[f.key] ?? ""}
                                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                                  placeholder={f.hint || ""}
                                  data-testid={`bulk-edit-${r.excel_row}-${f.label.toLowerCase()}`}/>
                              )
                            ) : (
                              <span>
                                {f.key === "Customer"    && (n.customer_resolved_name || o["Customer"] || o["Client / Subsidiary"] || "—")}
                                {f.key === "ABM ID"      && (n.abm_id      || o["ABM ID"] || "—")}
                                {f.key === "Asset Tag"   && (n.asset_tag   || o["Asset Tag"] || o["Asset ID / Tag"] || "—")}
                                {f.key === "Category"    && (n.category    || o["Category"] || "—")}
                                {f.key === "Product"     && (n.product     || o["Product"] || o["Device Name"] || "—")}
                                {f.key === "Serial"      && (n.serial      || o["Serial"] || o["Serial Number"] || "—")}
                                {f.key === "State"       && (n.status      || o["State"] || o["Status"] || "—")}
                                {f.key === "Warranty End"&& (n.warranty_end|| o["Warranty End"] || o["Warranty Expiry"] || "—")}
                                {f.key === "Country"     && (n.country     || o["Country"] || "—")}
                              </span>
                            )}
                          </td>
                        ))}

                        <td className="px-2 py-1.5 align-top">
                          {r.result === "READY" ? (
                            <span className="text-emerald-700 inline-flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Ready
                            </span>
                          ) : (
                            <span className="text-red-700 inline-flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" /> {r.result}
                            </span>
                          )}
                          {r.planned_action && (
                            <div className="mt-1">
                              <span
                                data-testid={`bulk-row-plan-${r.excel_row}`}
                                className={"inline-flex h-4 px-1.5 rounded border text-[10px] uppercase tracking-[0.14em] " +
                                  (PLAN_TONE[r.planned_action] || PLAN_TONE.SKIP)}>
                                {r.planned_action === "CREATE" ? "create"
                                  : r.planned_action === "UPDATE" ? "would update"
                                  : "skip"}
                              </span>
                            </div>
                          )}
                          {r.edited && !isEditing && (
                            <span className="ml-1 text-[10px] uppercase text-sky-700 font-medium tracking-wider">edited</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-red-700 align-top">
                          {r.errors?.map((e, i) => <div key={i}>• {e}</div>)}
                          {r.warnings?.map((w, i) => (
                            <div key={i} className="text-amber-700 flex items-start gap-2">
                              <span>• {w}</span>
                              {(w.toLowerCase().includes("customer") && w.toLowerCase().includes("not found")) && (
                                <button type="button"
                                  onClick={() => createMasterFromRow(r)}
                                  data-testid={`bulk-row-create-master-${r.excel_row}`}
                                  className="text-[10px] uppercase tracking-[0.06em] px-1.5 py-0.5 rounded border border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100">
                                  Create as new master
                                </button>
                              )}
                            </div>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 text-right align-top">
                          {isEditing ? (
                            <div className="inline-flex gap-1">
                              <Button size="sm" variant="ghost"
                                onClick={cancelEdit}
                                data-testid={`bulk-edit-cancel-${r.excel_row}`}>
                                <X className="h-3 w-3" />
                              </Button>
                              <Button size="sm"
                                onClick={() => saveEdit(r.excel_row)}
                                disabled={savingRow}
                                data-testid={`bulk-edit-save-${r.excel_row}`}>
                                {savingRow
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <><Save className="h-3 w-3 mr-1" /> Save</>}
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="ghost"
                              onClick={() => startEdit(r)}
                              data-testid={`bulk-edit-${r.excel_row}`}>
                              <Pencil className="h-3 w-3 mr-1" /> Fix
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Dialog open={bulkFixOpen} onOpenChange={setBulkFixOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-multi-fix-dialog">
            <DialogHeader>
              <DialogTitle>Fix {selectedRows.size} assignee(s)</DialogTitle>
              <div className="text-[12px] text-slate-500">
                The same assignee is applied to every selected row and
                each row is re-validated on the server.
              </div>
            </DialogHeader>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Target assignee
              </div>
              <AssigneeTypeahead
                value={bulkAssignee}
                onChange={setBulkAssignee}
                testId="bulk-multi-fix-typeahead"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setBulkFixOpen(false)}
                disabled={bulkBusy}>Cancel</Button>
              <Button onClick={runBulkFix}
                disabled={bulkBusy || !bulkAssignee.trim()}
                data-testid="bulk-multi-fix-apply">
                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            : <Wand2 className="h-3.5 w-3.5 mr-1" />}
                Apply to {selectedRows.size} row(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="bulk-cancel">
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
          <Button onClick={commit}
            disabled={!session || session.counts.ready === 0 || committing}
            data-testid="bulk-commit">
            {committing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> :
              <Upload className="h-3.5 w-3.5 mr-1.5" />}
            {session ? `Confirm & add ${session.counts.ready} devices`
                        : "Confirm & add devices"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
